import React, { useState, useRef } from 'react';
import axios from 'axios';
import { format, addDays, startOfWeek } from 'date-fns';
import { it } from 'date-fns/locale';
import styles from './UploadModal.module.css';

function mondayOf(date) {
  return startOfWeek(date, { weekStartsOn: 1 });
}

function defaultWeekDate() {
  const today = new Date();
  const monday = mondayOf(today);
  return format(monday, 'yyyy-MM-dd');
}

function weekPreview(dateStr) {
  if (!dateStr) return '';
  const start = new Date(dateStr + 'T00:00:00');
  const end = addDays(start, 6);
  return `${format(start, "d MMMM", { locale: it })} – ${format(end, "d MMMM yyyy", { locale: it })}`;
}

export default function UploadModal({ onClose, onSuccess, existingMenus }) {
  const [file, setFile] = useState(null);
  const [weekDate, setWeekDate] = useState(defaultWeekDate());
  const [note, setNote] = useState('');
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  const hasConflict = existingMenus.some(m => m.weekStart === weekDate);

  const handleFile = (f) => {
    if (!f) return;
    if (!f.name.endsWith('.html') && f.type !== 'text/html') {
      setError('Solo file HTML sono accettati.');
      return;
    }
    setError('');
    setFile(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files[0];
    handleFile(f);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) { setError('Seleziona un file HTML.'); return; }
    if (!weekDate) { setError('Seleziona la data della settimana.'); return; }

    setUploading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('weekDate', weekDate);
      fd.append('note', note);
      const { data } = await axios.post('/api/menus', fd);
      onSuccess(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Errore durante il caricamento.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className={styles.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Carica Menu</h2>
          <button className={styles.closeBtn} onClick={onClose} aria-label="Chiudi">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.field}>
            <label className={styles.label}>Settimana di riferimento</label>
            <input
              type="date"
              className={styles.input}
              value={weekDate}
              onChange={e => setWeekDate(e.target.value)}
              required
            />
            {weekDate && (
              <p className={styles.weekPreview}>{weekPreview(weekDate)}</p>
            )}
            {hasConflict && (
              <p className={styles.warning}>
                Esiste già un menu per questa settimana. Verrà sostituito.
              </p>
            )}
          </div>

          <div className={styles.field}>
            <label className={styles.label}>File HTML del menu</label>
            <div
              className={`${styles.dropzone} ${dragging ? styles.dropzoneDragging : ''} ${file ? styles.dropzoneHasFile : ''}`}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={handleDrop}
              onClick={() => inputRef.current?.click()}
            >
              <input
                ref={inputRef}
                type="file"
                accept=".html,text/html"
                style={{ display: 'none' }}
                onChange={e => handleFile(e.target.files[0])}
              />
              {file ? (
                <>
                  <svg className={styles.dropzoneIcon} style={{ color: 'var(--green)' }} width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  <p className={styles.dropzoneText} style={{ color: 'var(--green)' }}>{file.name}</p>
                  <p className={styles.dropzoneHint}>Clicca per cambiare file</p>
                </>
              ) : (
                <>
                  <svg className={styles.dropzoneIcon} width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                  <p className={styles.dropzoneText}>Trascina qui il file HTML</p>
                  <p className={styles.dropzoneHint}>oppure clicca per selezionarlo</p>
                </>
              )}
            </div>
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Note (opzionale)</label>
            <input
              type="text"
              className={styles.input}
              placeholder="Es. Settimana speciale, menù estivo…"
              value={note}
              onChange={e => setNote(e.target.value)}
              maxLength={120}
            />
          </div>

          {error && <p className={styles.error}>{error}</p>}

          <div className={styles.modalFooter}>
            <button type="button" className={styles.btnCancel} onClick={onClose}>Annulla</button>
            <button type="submit" className={styles.btnSubmit} disabled={uploading}>
              {uploading ? 'Caricamento…' : hasConflict ? 'Sostituisci menu' : 'Carica menu'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
